import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatNumber } from '../lib/format';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead } from '../ui/table';
import { DefList, Num, Tag, Time } from '../ui/values';
import { paths } from './paths';

/** Übersicht (design.md §6): Kennzahlenzeile, Systemzustand, Sync-Probleme 24 h, letzte Ereignisse. Keine Diagramme. */

interface Overview {
  users: { pendingBeta: number; active: number; suspended: number; admins: number };
  teams: number;
  chests: number;
  chestItems: number;
  itemVariants: number;
  syncErrors24h: number;
  db: 'ok' | 'down';
  system: { dbLatencyMs: number | null; mojang: 'none' | 'mojang'; lastCleanup: { at: string; ok: boolean } | null; checkedAt: string };
  syncProblems: { userId: string | null; userName: string | null; code: string; count: number; lastAt: string }[];
  latestEvents: { id: string; source: 'event' | 'audit'; level: 'info' | 'warn' | 'error' | 'admin'; category: string; message: string; userId: string | null; createdAt: string }[];
}

export const Level = ({ level }: { level: string }) => <span className={`lvl lvl-${level}`}>{level.toUpperCase()}</span>;

export function OverviewPage() {
  const q = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.get<Overview>('/admin/overview'), refetchInterval: 60_000 });
  const o = q.data;

  return (
    <div className="stack-lg">
      <PageHead title="Übersicht" context={o ? `Stand ${new Date(o.system.checkedAt).toLocaleTimeString('de-DE')}` : undefined} />
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {o && (
        <>
          <dl className="kpis">
            <Kpi label="Spieler aktiv" value={o.users.active} />
            <Kpi label="gesperrt" value={o.users.suspended} />
            <Kpi label="Teams" value={o.teams} />
            <Kpi label="Kisten" value={o.chests} />
            <Kpi label="Kisten-Item-Einträge" value={o.chestItems} />
            <Kpi label="Item-Varianten" value={o.itemVariants} />
          </dl>

          <div className="cols-2">
            <section className="stack">
              <h2>Systemzustand</h2>
              <DefList
                items={[
                  ['API', <Tag tone="ok">erreichbar</Tag>],
                  [
                    'Datenbank',
                    o.db === 'ok' ? (
                      <span>
                        <Tag tone="ok">ok</Tag> <span className="muted">{o.system.dbLatencyMs} ms</span>
                      </span>
                    ) : (
                      <Tag tone="err">nicht erreichbar</Tag>
                    ),
                  ],
                  ['Discord-Login', <Tag tone="neutral">konfiguriert</Tag>],
                  [
                    'Mojang-Prüfung',
                    o.system.mojang === 'mojang' ? <Tag tone="ok">aktiv</Tag> : <Tag tone="warn">aus (Beta)</Tag>,
                  ],
                  [
                    'Aufräumjob',
                    o.system.lastCleanup ? (
                      <span>
                        <Tag tone={o.system.lastCleanup.ok ? 'ok' : 'err'}>{o.system.lastCleanup.ok ? 'ok' : 'Fehler'}</Tag>{' '}
                        <span className="muted">
                          <Time iso={o.system.lastCleanup.at} />
                        </span>
                      </span>
                    ) : (
                      <span className="muted">noch nicht gelaufen</span>
                    ),
                  ],
                ]}
              />
            </section>

            <section className="stack">
              <h2>
                Sync-Probleme (24 h) <span className="muted">{formatNumber(o.syncErrors24h)}</span>
              </h2>
              <DataTable
                label="Sync-Probleme der letzten 24 Stunden"
                rows={o.syncProblems}
                rowKey={(r) => `${r.userId}:${r.code}`}
                rowHref={(r) => (r.userId ? paths.player(r.userId) : paths.logs)}
                empty="Keine Sync-Probleme in den letzten 24 Stunden."
                columns={[
                  { key: 'user', header: 'Spieler', cell: (r) => r.userName ?? <span className="muted">unbekannt</span> },
                  { key: 'code', header: 'Fehlercode', cell: (r) => <span className="mono">{r.code}</span> },
                  { key: 'n', header: 'Anzahl', num: true, cell: (r) => <Num value={r.count} /> },
                  { key: 'last', header: 'zuletzt', cell: (r) => <Time iso={r.lastAt} /> },
                ]}
              />
            </section>
          </div>

          <section className="stack">
            <div className="section-head">
              <h2>Letzte Ereignisse</h2>
              <Link to={paths.logs}>Alle Protokolle</Link>
            </div>
            <DataTable
              label="Letzte Warnungen, Fehler und Admin-Aktionen"
              rows={o.latestEvents}
              rowKey={(r) => r.id}
              empty="Keine Warnungen, Fehler oder Admin-Aktionen."
              columns={[
                { key: 'lvl', header: 'Level', cell: (r) => <Level level={r.level} /> },
                { key: 'cat', header: 'Kategorie', cell: (r) => <span className="mono">{r.category}</span> },
                { key: 'msg', header: 'Meldung', cell: (r) => r.message || <span className="muted">–</span> },
                { key: 'at', header: 'Zeit', cell: (r) => <Time iso={r.createdAt} /> },
              ]}
            />
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="kpi">
      <dt>{label}</dt>
      <dd>
        <Num value={value} />
      </dd>
    </div>
  );
}
